"use client";

import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { isUnimplementedNativeCall } from "@/lib/native/bridge-availability";
import {
  applyDisplayState,
  HiiveDisplay,
  type NativeDisplayState,
} from "@/lib/native/display";

/**
 * Keeps `<html>` in step with the iOS shell's display report — iPhone Duo's
 * fold, posture and active display (see `lib/native/display.ts`). Renders
 * nothing; a no-op on the web, Android and any binary without the plugin.
 *
 * Re-reads on `visibilitychange` and `resize` as well as the plugin's own
 * event: moving between Duo's outer and inner display pauses the web view, so
 * an event fired mid-fold can arrive late or be superseded by the next layout.
 */
export function NativeDisplaySync() {
  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios") return;
    const root = document.documentElement;
    let disposed = false;
    let removeListener: (() => Promise<void>) | null = null;

    const apply = (state: NativeDisplayState | null) => {
      if (!disposed) applyDisplayState(root, state);
    };
    const refresh = () => {
      HiiveDisplay.getState().then(apply, (error: unknown) => {
        // An older binary: no plugin, so no fold — the plain layout stands.
        if (isUnimplementedNativeCall(error)) apply(null);
      });
    };

    refresh();
    void HiiveDisplay.addListener("change", apply)
      .then((handle) => {
        if (disposed) void handle.remove();
        else removeListener = handle.remove;
      })
      .catch(() => {});
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("resize", refresh);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", refresh);
      void removeListener?.();
      applyDisplayState(root, null);
    };
  }, []);

  return null;
}
